import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { IconBarChart, IconRefreshCw, IconDownload } from '../components/Icons';
import { api } from '../services/api';
import ProductHistoryModal from '../components/ProductHistoryModal';
import ActionTypesModal, { getActionTypeLabel } from '../components/ActionTypesModal';
import ActionTypeHistoryModal from '../components/ActionTypeHistoryModal';
import TzProductDetailModal, { TzConfirmedTableRow } from '../components/TzProductDetailModal';
import SubjectDetailModal from '../components/SubjectDetailModal';
import SummaryDetailModal from '../components/SummaryDetailModal';
import { useAppSelector } from '../store/hooks';
import { formatDateRu } from '../utils/dateFormat';
import { formatSubjectName } from '../utils/formatSubjectName';
import { hasRole } from '../utils/roleList';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function getCurrentWeekRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysSinceMonday);
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  const dateFrom = monday.toISOString().slice(0, 10);
  const dateTo = nextMonday.toISOString().slice(0, 10);
  return { dateFrom, dateTo };
}

const Statistics: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = hasRole(user?.role, 'admin');
  const { dateFrom: defaultFrom, dateTo: defaultTo } = getCurrentWeekRange();
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ inWork: number; statusChanges: number; newProducts: number; tzConfirmed: number } | null>(null);
  const [byDay, setByDay] = useState<{ date: string; statusChanges: number; newProducts: number; tzConfirmed: number }[]>([]);
  const [byActionType, setByActionType] = useState<{ action_type: string; count: number }[]>([]);
  const [bySubject, setBySubject] = useState<{ subject_id: number; subject_name: string; inWork: number; statusChanges: number; newProducts: number; tzConfirmed: number }[]>([]);
  const [tzConfirmedArticles, setTzConfirmedArticles] = useState<TzConfirmedTableRow[]>([]);
  const [showTzProductModal, setShowTzProductModal] = useState(false);
  const [selectedTzProduct, setSelectedTzProduct] = useState<TzConfirmedTableRow | null>(null);
  const [historyProductId, setHistoryProductId] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [analysts, setAnalysts] = useState<{ id: string; username: string; email: string }[]>([]);
  const [showActionTypesModal, setShowActionTypesModal] = useState(false);
  const [showActionTypeHistoryModal, setShowActionTypeHistoryModal] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState<string>('');
  type SubjectSortKey = 'subject_name' | 'inWork' | 'statusChanges' | 'newProducts' | 'tzConfirmed';
  const [subjectSortBy, setSubjectSortBy] = useState<SubjectSortKey>('subject_name');
  const [subjectSortDir, setSubjectSortDir] = useState<'asc' | 'desc'>('desc');
  const [showSubjectDetailModal, setShowSubjectDetailModal] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [selectedSubjectName, setSelectedSubjectName] = useState<string>('');
  const [subjectSearchInput, setSubjectSearchInput] = useState<string>('');
  const [showSummaryDetailModal, setShowSummaryDetailModal] = useState(false);
  const [summaryDetailMetric, setSummaryDetailMetric] = useState<'inWork' | 'statusChanges' | 'newProducts' | 'tzConfirmed' | null>(null);

  const summaryMetricLabels: Record<'inWork' | 'statusChanges' | 'newProducts' | 'tzConfirmed', string> = {
    inWork: 'В работе',
    statusChanges: 'Изменения статуса',
    newProducts: 'Новые товары',
    tzConfirmed: 'Подготовлено ТЗ'
  };

  const handleSubjectSort = (key: SubjectSortKey) => {
    if (subjectSortBy === key) {
      setSubjectSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSubjectSortBy(key);
      setSubjectSortDir('desc');
    }
  };

  const sortedBySubject = useMemo(() => {
    if (!bySubject.length) return [];
    const key = subjectSortBy;
    const dir = subjectSortDir === 'asc' ? 1 : -1;
    return [...bySubject].sort((a, b) => {
      const va = key === 'subject_name' ? (a.subject_name || '').toLowerCase() : (a[key] ?? 0);
      const vb = key === 'subject_name' ? (b.subject_name || '').toLowerCase() : (b[key] ?? 0);
      if (typeof va === 'string' && typeof vb === 'string') return dir * va.localeCompare(vb);
      return dir * (Number(va) - Number(vb));
    });
  }, [bySubject, subjectSortBy, subjectSortDir]);

  const filteredBySubject = useMemo(() => {
    if (!subjectSearchInput.trim()) return sortedBySubject;
    const q = subjectSearchInput.trim().toLowerCase();
    return sortedBySubject.filter((row) => (row.subject_name || '').toLowerCase().includes(q));
  }, [sortedBySubject, subjectSearchInput]);

  const loadData = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    try {
      const userId = isAdmin && selectedUserId ? selectedUserId : undefined;
      const data = await api.getWeeklyStatistics(dateFrom, dateTo, userId);
      setSummary(data.summary);
      setByDay(data.byDay);
      setByActionType(data.byActionType || []);
      setBySubject(data.bySubject || []);
      setTzConfirmedArticles(data.tzConfirmedArticles);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
      setSummary(null);
      setByDay([]);
      setByActionType([]);
      setBySubject([]);
      setTzConfirmedArticles([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, isAdmin, selectedUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (isAdmin) {
      api.getAnalystUsers().then((res: any) => setAnalysts(Array.isArray(res?.data) ? res.data : [])).catch(() => setAnalysts([]));
    }
  }, [isAdmin]);

  const handleExport = async () => {
    if (!dateFrom || !dateTo) return;
    setExportLoading(true);
    try {
      const userId = isAdmin && selectedUserId ? selectedUserId : undefined;
      const blob = await api.getWeeklyStatisticsExport(dateFrom, dateTo, userId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_analytics_${dateFrom}_${dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка выгрузки');
    } finally {
      setExportLoading(false);
    }
  };

  const chartData = byDay.map(d => ({
    ...d,
    dateShort: formatDateRu(d.date),
    Изменения_статуса: d.statusChanges,
    Новые_товары: d.newProducts,
    Подготовлено_ТЗ: d.tzConfirmed
  }));

  const actionTypesTotal = byActionType.reduce((sum, row) => sum + row.count, 0);
  const periodLabel = dateFrom && dateTo ? `Период: ${formatDateRu(dateFrom)} — ${formatDateRu(dateTo)}` : undefined;

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        <IconBarChart className="me-2" />
        Статистика
      </h2>

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <Form.Group className="mb-0 d-flex align-items-center gap-2">
              <Form.Label className="mb-0">С</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ width: 'auto' }}
              />
            </Form.Group>
            <Form.Group className="mb-0 d-flex align-items-center gap-2">
              <Form.Label className="mb-0">По</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ width: 'auto' }}
              />
            </Form.Group>
            {isAdmin && (
              <Form.Group className="mb-0 d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Пользователь</Form.Label>
                <Form.Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  style={{ width: 'auto', minWidth: 180 }}
                >
                  <option value="">Я</option>
                  {analysts.map((a) => (
                    <option key={a.id} value={a.id}>{a.username || a.email || a.id}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Button variant="primary" onClick={loadData} disabled={loading}>
              <IconRefreshCw className={loading ? 'fa-spin' : ''} />
              Обновить
            </Button>
            <Button variant="outline-success" onClick={handleExport} disabled={exportLoading || !dateFrom || !dateTo}>
              <IconDownload className={exportLoading ? 'fa-spin' : ''} />
              {exportLoading ? ' Выгрузка...' : ' Скачать отчёт'}
            </Button>
          </div>
        </Card.Header>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !summary && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && summary && (
        <Tabs defaultActiveKey="summary" id="statistics-tabs" className="mb-3">
          <Tab eventKey="summary" title="Сводка">
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setSummaryDetailMetric('inWork'); setShowSummaryDetailModal(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDetailMetric('inWork'); setShowSummaryDetailModal(true); } }}
                >
                  <Card.Body>
                    <Card.Title className="small text-muted">В работе</Card.Title>
                    <p className="mb-0 fs-4">{summary.inWork}</p>
                    <span className="small text-muted">Клик — потоварная детализация</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setSummaryDetailMetric('statusChanges'); setShowSummaryDetailModal(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDetailMetric('statusChanges'); setShowSummaryDetailModal(true); } }}
                >
                  <Card.Body>
                    <Card.Title className="small text-muted">Изменения статуса</Card.Title>
                    <p className="mb-0 fs-4">{summary.statusChanges}</p>
                    <span className="small text-muted">Клик — потоварная детализация</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setSummaryDetailMetric('newProducts'); setShowSummaryDetailModal(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDetailMetric('newProducts'); setShowSummaryDetailModal(true); } }}
                >
                  <Card.Body>
                    <Card.Title className="small text-muted">Новые товары</Card.Title>
                    <p className="mb-0 fs-4">{summary.newProducts}</p>
                    <span className="small text-muted">Клик — потоварная детализация</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setSummaryDetailMetric('tzConfirmed'); setShowSummaryDetailModal(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDetailMetric('tzConfirmed'); setShowSummaryDetailModal(true); } }}
                >
                  <Card.Body>
                    <Card.Title className="small text-muted">Подготовлено ТЗ</Card.Title>
                    <p className="mb-0 fs-4">{summary.tzConfirmed}</p>
                    <span className="small text-muted">Клик — потоварная детализация</span>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <SummaryDetailModal
              show={showSummaryDetailModal}
              onHide={() => { setShowSummaryDetailModal(false); setSummaryDetailMetric(null); }}
              metric={summaryDetailMetric}
              metricLabel={summaryDetailMetric ? summaryMetricLabels[summaryDetailMetric] : ''}
              dateFrom={dateFrom}
              dateTo={dateTo}
              userId={isAdmin && selectedUserId ? selectedUserId : undefined}
            />

            {chartData.length > 0 && (
            <Card className="mb-4">
              <Card.Header>По дням</Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateShort" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Изменения_статуса" stroke="#8884d8" name="Изменения статуса" />
                    <Line type="monotone" dataKey="Новые_товары" stroke="#82ca9d" name="Новые товары" />
                    <Line type="monotone" dataKey="Подготовлено_ТЗ" stroke="#ffc658" name="Подготовлено ТЗ" />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          )}

          <Card
            className="mb-4 cursor-pointer"
            onClick={() => setShowActionTypesModal(true)}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="d-flex align-items-center justify-content-between">
              <div>
                <Card.Title className="small text-muted mb-1">События по типам действий</Card.Title>
                <p className="mb-0 fs-5">
                  {byActionType.length === 0
                    ? 'Нет событий за период'
                    : `Всего событий: ${actionTypesTotal} (${byActionType.length} типов)`}
                </p>
              </div>
              <Button variant="outline-primary" size="sm" onClick={(e) => { e.stopPropagation(); setShowActionTypesModal(true); }}>
                Подробнее
              </Button>
            </Card.Body>
          </Card>

          <ActionTypesModal
            show={showActionTypesModal}
            onHide={() => setShowActionTypesModal(false)}
            data={byActionType}
            periodLabel={periodLabel}
            onSelectActionType={(actionType) => {
              setSelectedActionType(actionType);
              setShowActionTypesModal(false);
              setShowActionTypeHistoryModal(true);
            }}
          />
          <ActionTypeHistoryModal
            show={showActionTypeHistoryModal}
            onHide={() => setShowActionTypeHistoryModal(false)}
            actionType={selectedActionType}
            actionTypeLabel={getActionTypeLabel(selectedActionType)}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />

            <Card className="mb-4">
              <Card.Header>История изменений по товару</Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Form.Control
                  type="text"
                  placeholder="ID товара"
                  value={historyProductId}
                  onChange={(e) => setHistoryProductId(e.target.value)}
                  style={{ maxWidth: 200 }}
                />
                <Button
                  variant="primary"
                  onClick={() => historyProductId.trim() && setShowHistoryModal(true)}
                  disabled={!historyProductId.trim()}
                >
                  Показать историю
                </Button>
              </div>
            </Card.Body>
            </Card>

              <ProductHistoryModal
              show={showHistoryModal}
              onHide={() => setShowHistoryModal(false)}
              productId={historyProductId.trim()}
              productLabel={historyProductId.trim() ? `ID ${historyProductId.trim()}` : undefined}
            />

            <Card>
              <Card.Header>Подготовлено ТЗ по артикулам</Card.Header>
              <Card.Body>
                {tzConfirmedArticles.length === 0 ? (
                  <p className="text-muted mb-0">Нет записей за выбранный период.</p>
                ) : (
                  <>
                    <p className="text-muted small mb-2">Клик по строке — карточка товара и история (без редактирования).</p>
                    <Table responsive variant="dark" className="table-dark" size="sm" hover>
                      <thead>
                        <tr>
                          <th>SKU / Артикул</th>
                          <th>Название</th>
                          <th>Тип</th>
                          <th>Изображения</th>
                          <th>Упаковка</th>
                          <th>Сезонность</th>
                          <th>Описание</th>
                          <th>Описание (EN)</th>
                          <th>Предмет</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tzConfirmedArticles.map((row) => {
                          const getFullImageUrl = (u: string | null | undefined): string => {
                            if (!u || typeof u !== 'string' || !u.trim()) return '';
                            const url = u.trim();
                            if (url.startsWith('http')) return url;
                            if (url.startsWith('//')) return `https:${url}`;
                            if (url.startsWith('/')) return url;
                            return url;
                          };
                          const thumb = row.thumb_middle_url || row.thumb_url || row.image_url_1;
                          const imgUrls = [thumb, row.image_url_1, row.image_url_2, row.image_url_3, row.image_url_4]
                            .filter(Boolean)
                            .map(getFullImageUrl)
                            .filter((url): url is string => !!url)
                            .filter((url, i, arr) => arr.indexOf(url) === i);
                          const descShort = row.desc ? (row.desc.length > 80 ? row.desc.slice(0, 80) + '…' : row.desc) : '—';
                          const engDescShort = row.eng_desc ? (row.eng_desc.length > 80 ? row.eng_desc.slice(0, 80) + '…' : row.eng_desc) : '—';
                          return (
                            <tr
                              key={row.id}
                              onClick={() => { setSelectedTzProduct(row); setShowTzProductModal(true); }}
                              style={{ cursor: 'pointer', minHeight: '80px' }}
                              role="button"
                            >
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  <span className="text-muted small">{row.id}</span>
                                  {row.article && <span>{row.article}</span>}
                                  {!row.article && <span className="text-muted">—</span>}
                                </div>
                              </td>
                              <td style={{ width: '220px', minWidth: '180px', maxWidth: '280px' }}>
                                <div className="fw-bold text-break" style={{ whiteSpace: 'pre-wrap' }}>{row.name || '—'}</div>
                              </td>
                              <td>
                                {row.new_wave ? <span className="badge bg-info">New Wave</span> : <span className="text-muted">—</span>}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {imgUrls.length > 0 ? imgUrls.map((url, idx) => (
                                    <img
                                      key={idx}
                                      src={url}
                                      alt=""
                                      referrerPolicy="no-referrer"
                                      style={{ maxHeight: 48, width: 'auto', objectFit: 'contain' }}
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  )) : <span className="text-muted">—</span>}
                                </div>
                              </td>
                              <td className="small text-break" style={{ maxWidth: 160 }}>{row.package_summary?.trim() ? row.package_summary : '—'}</td>
                              <td>{row.season ?? '—'}</td>
                              <td className="small text-break" style={{ maxWidth: 180, whiteSpace: 'pre-wrap' }}>{descShort}</td>
                              <td className="small text-break" style={{ maxWidth: 180, whiteSpace: 'pre-wrap' }}>{engDescShort}</td>
                              <td>{formatSubjectName(row.subject_name)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
            <TzProductDetailModal
              show={showTzProductModal}
              onHide={() => { setShowTzProductModal(false); setSelectedTzProduct(null); }}
              product={selectedTzProduct}
            />
          </Tab>

          <Tab eventKey="subjects" title="По предметам">
            <Card>
              <Card.Header>Статистика по предметам</Card.Header>
              <Card.Body>
                {bySubject.length === 0 ? (
                  <p className="text-muted mb-0">Нет данных по предметам.</p>
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Поиск по названию предмета..."
                        value={subjectSearchInput}
                        onChange={(e) => setSubjectSearchInput(e.target.value)}
                        style={{ maxWidth: 320 }}
                      />
                      {subjectSearchInput.trim() && (
                        <Form.Text className="text-muted">
                          Показано {filteredBySubject.length} из {sortedBySubject.length}
                        </Form.Text>
                      )}
                    </Form.Group>
                  <Table responsive variant="dark" size="sm" hover>
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSubjectSort('subject_name')}
                          title="Сортировать по предмету"
                        >
                          Предмет {subjectSortBy === 'subject_name' && (subjectSortDir === 'asc' ? ' ↑' : ' ↓')}
                        </th>
                        <th
                          className="text-end"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSubjectSort('inWork')}
                          title="Сортировать по «В работе»"
                        >
                          В работе {subjectSortBy === 'inWork' && (subjectSortDir === 'asc' ? ' ↑' : ' ↓')}
                        </th>
                        <th
                          className="text-end"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSubjectSort('statusChanges')}
                          title="Сортировать по изменениям статуса"
                        >
                          Изменения статуса {subjectSortBy === 'statusChanges' && (subjectSortDir === 'asc' ? ' ↑' : ' ↓')}
                        </th>
                        <th
                          className="text-end"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSubjectSort('newProducts')}
                          title="Сортировать по новым товарам"
                        >
                          Новые товары {subjectSortBy === 'newProducts' && (subjectSortDir === 'asc' ? ' ↑' : ' ↓')}
                        </th>
                        <th
                          className="text-end"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSubjectSort('tzConfirmed')}
                          title="Сортировать по подготовленным ТЗ"
                        >
                          Подготовлено ТЗ {subjectSortBy === 'tzConfirmed' && (subjectSortDir === 'asc' ? ' ↑' : ' ↓')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBySubject.map((row) => (
                        <tr
                          key={row.subject_id}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedSubjectId(row.subject_id);
                            setSelectedSubjectName(row.subject_name || '');
                            setShowSubjectDetailModal(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedSubjectId(row.subject_id);
                              setSelectedSubjectName(row.subject_name || '');
                              setShowSubjectDetailModal(true);
                            }
                          }}
                        >
                          <td>{formatSubjectName(row.subject_name)}</td>
                          <td className="text-end">{row.inWork}</td>
                          <td className="text-end">{row.statusChanges}</td>
                          <td className="text-end">{row.newProducts}</td>
                          <td className="text-end">{row.tzConfirmed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </>
                )}
              </Card.Body>
            </Card>
            <SubjectDetailModal
              show={showSubjectDetailModal}
              onHide={() => { setShowSubjectDetailModal(false); setSelectedSubjectId(null); setSelectedSubjectName(''); }}
              subjectId={selectedSubjectId}
              subjectName={selectedSubjectName}
              dateFrom={dateFrom}
              dateTo={dateTo}
              userId={isAdmin && selectedUserId ? selectedUserId : undefined}
            />
          </Tab>
        </Tabs>
      )}
    </Container>
  );
};

export default Statistics;
